// src/components/settings/DiscordOnboardingWizard.jsx
import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { settingsAPI } from '../../services/api';
import {
    CheckCircleIcon,
    ExclamationTriangleIcon,
    InformationCircleIcon,
    ArrowRightIcon,
    BellIcon,
    LinkIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const STEPS = {
    WELCOME: 'welcome',
    SETUP: 'setup',
    WEBHOOK: 'webhook',
    VERIFICATION: 'verification',
    COMPLETE: 'complete'
};

const DiscordOnboardingWizard = ({ onComplete, onClose }) => {
    const [currentStep, setCurrentStep] = useState(STEPS.WELCOME);
    const [webhookUrl, setWebhookUrl] = useState('');
    const [validationResult, setValidationResult] = useState(null);
    const [isTestingConnection, setIsTestingConnection] = useState(false);

    const queryClient = useQueryClient();

    // Save credentials mutation
    const saveCredentialsMutation = useMutation(
        (creds) => settingsAPI.saveCredentials(creds),
        {
            onSuccess: () => {
                toast.success('Discord webhook saved!');
                queryClient.invalidateQueries('credentials');
            },
            onError: (error) => {
                toast.error('Failed to save webhook: ' + error.message);
            }
        }
    );

    // Test connection mutation
    const testConnectionMutation = useMutation(
        () => settingsAPI.testDiscord(),
        {
            onSuccess: (data) => {
                setValidationResult(data);
                setCurrentStep(STEPS.COMPLETE);
                toast.success('Discord webhook verified!');
            },
            onError: (error) => {
                setValidationResult({ success: false, message: error.message });
                toast.error('Connection test failed: ' + error.message);
            }
        }
    );

    const handleSaveAndTest = async () => {
        setIsTestingConnection(true);
        try {
            // Save webhook URL first
            await saveCredentialsMutation.mutateAsync({ DISCORD_WEBHOOK_URL: webhookUrl });

            // Then test the connection
            await testConnectionMutation.mutateAsync();
        } catch (error) {
            console.error('Failed to save and test:', error);
        } finally {
            setIsTestingConnection(false);
        }
    };

    const isStepComplete = (step) => {
        switch (step) {
            case STEPS.WELCOME:
                return true;
            case STEPS.SETUP:
                return true;
            case STEPS.WEBHOOK:
                return webhookUrl && webhookUrl.includes('discord.com/api/webhooks/');
            case STEPS.VERIFICATION:
                return validationResult?.success;
            default:
                return false;
        }
    };

    const renderStepContent = () => {
        switch (currentStep) {
            case STEPS.WELCOME:
                return (
                    <div className="space-y-6">
                        <div className="text-center">
                            <div className="mx-auto w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mb-4">
                                <BellIcon className="h-8 w-8 text-indigo-600" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">
                                Set Up Discord Notifications
                            </h2>
                            <p className="text-gray-600 max-w-md mx-auto">
                                Get real-time notifications in your Discord server when posts are published across your social platforms.
                            </p>
                        </div>

                        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                            <div className="flex items-start">
                                <InformationCircleIcon className="h-5 w-5 text-indigo-400 mt-0.5 mr-3" />
                                <div className="text-sm text-indigo-800">
                                    <p className="font-medium mb-1">What you'll get:</p>
                                    <ul className="list-disc list-inside space-y-1">
                                        <li>Real-time post publishing notifications</li>
                                        <li>Success and error alerts</li>
                                        <li>Platform-specific posting summaries</li>
                                        <li>Easy monitoring of your automation</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-between">
                            <button
                                onClick={onClose}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => setCurrentStep(STEPS.SETUP)}
                                className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center"
                            >
                                Get Started
                                <ArrowRightIcon className="ml-2 h-4 w-4" />
                            </button>
                        </div>
                    </div>
                );

            case STEPS.SETUP:
                return (
                    <div className="space-y-6">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 mb-2">
                                Create a Discord Webhook
                            </h2>
                            <p className="text-gray-600">
                                We'll walk you through creating a webhook in your Discord server.
                            </p>
                        </div>

                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                            <h3 className="font-medium text-gray-900 mb-3">Step-by-step instructions:</h3>
                            <ol className="list-decimal list-inside space-y-3 text-sm text-gray-700">
                                <li>
                                    <strong>Open Discord</strong> and navigate to your server
                                </li>
                                <li>
                                    <strong>Right-click on a text channel</strong> where you want notifications (e.g., #social-media)
                                </li>
                                <li>
                                    Select <strong>"Edit Channel"</strong> from the context menu
                                </li>
                                <li>
                                    Go to the <strong>"Integrations"</strong> tab
                                </li>
                                <li>
                                    Click <strong>"Create Webhook"</strong>
                                </li>
                                <li>
                                    Give it a name like <strong>"Social Media Bot"</strong>
                                </li>
                                <li>
                                    Click <strong>"Copy Webhook URL"</strong>
                                </li>
                                <li>
                                    Paste the URL in the next step
                                </li>
                            </ol>
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="flex items-start">
                                <InformationCircleIcon className="h-5 w-5 text-blue-400 mt-0.5 mr-3" />
                                <div className="text-sm text-blue-800">
                                    <p className="font-medium mb-1">Pro tip:</p>
                                    <p>Create a dedicated channel like #social-media-updates to keep notifications organized and avoid cluttering your main channels.</p>
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-between">
                            <button
                                onClick={() => setCurrentStep(STEPS.WELCOME)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                            >
                                Back
                            </button>
                            <button
                                onClick={() => setCurrentStep(STEPS.WEBHOOK)}
                                className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center"
                            >
                                I've Created the Webhook
                                <ArrowRightIcon className="ml-2 h-4 w-4" />
                            </button>
                        </div>
                    </div>
                );

            case STEPS.WEBHOOK:
                return (
                    <div className="space-y-6">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 mb-2">
                                Enter Your Webhook URL
                            </h2>
                            <p className="text-gray-600">
                                Paste the webhook URL you copied from Discord.
                            </p>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Discord Webhook URL *
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <LinkIcon className="h-4 w-4 text-gray-400" />
                                    </div>
                                    <input
                                        type="url"
                                        value={webhookUrl}
                                        onChange={(e) => setWebhookUrl(e.target.value)}
                                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        placeholder="https://discord.com/api/webhooks/..."
                                    />
                                </div>
                                <p className="mt-1 text-xs text-gray-500">
                                    Should start with https://discord.com/api/webhooks/
                                </p>
                            </div>
                        </div>

                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <div className="flex items-start">
                                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mt-0.5 mr-3" />
                                <div className="text-sm text-yellow-800">
                                    <p className="font-medium mb-1">Keep your webhook secure:</p>
                                    <ul className="list-disc list-inside space-y-1">
                                        <li>Don't share your webhook URL publicly</li>
                                        <li>Anyone with this URL can send messages to your channel</li>
                                        <li>You can regenerate the webhook URL anytime in Discord</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-between">
                            <button
                                onClick={() => setCurrentStep(STEPS.SETUP)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                            >
                                Back
                            </button>
                            <button
                                onClick={() => setCurrentStep(STEPS.VERIFICATION)}
                                disabled={!isStepComplete(STEPS.WEBHOOK)}
                                className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center"
                            >
                                Continue
                                <ArrowRightIcon className="ml-2 h-4 w-4" />
                            </button>
                        </div>
                    </div>
                );

            case STEPS.VERIFICATION:
                return (
                    <div className="space-y-6">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 mb-2">
                                Test Your Webhook
                            </h2>
                            <p className="text-gray-600">
                                Let's send a test message to make sure everything is working.
                            </p>
                        </div>

                        {validationResult && !validationResult.success && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                <div className="flex items-start">
                                    <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mt-0.5 mr-3" />
                                    <div>
                                        <h3 className="text-sm font-medium text-red-800">Test Failed</h3>
                                        <p className="text-sm text-red-700 mt-1">{validationResult.message}</p>
                                        <div className="mt-2 text-xs text-red-600">
                                            <p>Common issues:</p>
                                            <ul className="list-disc list-inside mt-1">
                                                <li>Invalid webhook URL format</li>
                                                <li>Webhook was deleted or disabled</li>
                                                <li>Insufficient permissions in Discord</li>
                                                <li>Network connectivity issues</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <div className="flex items-start">
                                <CheckCircleIcon className="h-5 w-5 text-green-400 mt-0.5 mr-3" />
                                <div className="text-sm text-green-800">
                                    <p className="font-medium mb-1">What will happen:</p>
                                    <p>We'll send a test message to your Discord channel. You should see a message from your webhook confirming the connection is working.</p>
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-between">
                            <button
                                onClick={() => setCurrentStep(STEPS.WEBHOOK)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                            >
                                Back
                            </button>
                            <button
                                onClick={handleSaveAndTest}
                                disabled={isTestingConnection}
                                className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-indigo-400 flex items-center"
                            >
                                {isTestingConnection ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                        Sending Test...
                                    </>
                                ) : (
                                    <>
                                        Send Test Message
                                        <ArrowRightIcon className="ml-2 h-4 w-4" />
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                );

            case STEPS.COMPLETE:
                return (
                    <div className="space-y-6 text-center">
                        <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                            <CheckCircleIcon className="h-8 w-8 text-green-600" />
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">
                                Discord Notifications Ready!
                            </h2>
                            <p className="text-gray-600">
                                Your Discord webhook is now configured and ready to receive notifications.
                            </p>
                        </div>

                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <div className="text-sm text-green-800">
                                <p className="font-medium">What's Next:</p>
                                <ul className="mt-2 space-y-1 text-left">
                                    <li>✅ You'll receive notifications when posts are published</li>
                                    <li>✅ Error alerts if something goes wrong</li>
                                    <li>✅ Daily summaries of your social media activity</li>
                                    <li>🔧 You can modify the webhook anytime in Settings</li>
                                </ul>
                            </div>
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="text-sm text-blue-800">
                                <p className="font-medium">Example notification:</p>
                                <div className="mt-2 bg-white rounded p-2 border text-left">
                                    <div className="flex items-center space-x-2">
                                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                        <span className="font-medium">Post Published Successfully</span>
                                    </div>
                                    <p className="text-xs text-gray-600 mt-1">
                                        Published to LinkedIn, Twitter • 2:34 PM
                                    </p>
                                </div>
                            </div>
                        </div>

                        <button
                            onClick={onComplete}
                            className="w-full px-6 py-3 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center justify-center"
                        >
                            Complete Setup
                            <ArrowRightIcon className="ml-2 h-5 w-5" />
                        </button>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                {/* Background overlay */}
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

                {/* Modal panel */}
                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
                    {/* Progress bar */}
                    <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                        <div className="flex items-center justify-between mb-2">
                            <h1 className="text-lg font-medium text-gray-900">Discord Setup</h1>
                            <button
                                onClick={onClose}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                ✕
                            </button>
                        </div>

                        <div className="flex items-center space-x-2">
                            {Object.values(STEPS).map((step, index) => (
                                <React.Fragment key={step}>
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
                                        currentStep === step
                                            ? 'bg-indigo-600 text-white'
                                            : isStepComplete(step)
                                                ? 'bg-green-500 text-white'
                                                : 'bg-gray-200 text-gray-600'
                                    }`}>
                                        {isStepComplete(step) ? '✓' : index + 1}
                                    </div>
                                    {index < Object.values(STEPS).length - 1 && (
                                        <div className={`h-1 w-6 rounded ${
                                            isStepComplete(step) ? 'bg-green-500' : 'bg-gray-200'
                                        }`} />
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>

                    {/* Content */}
                    <div className="px-6 py-6">
                        {renderStepContent()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DiscordOnboardingWizard;